// Fahrpreis-Schätzung nach Unternehmens-Tarif (inkl. Nachttarif) für die
// Portal-Anzeige. Spiegelt AppSettings.isNachtTarif/effective* der Mobile-App
// und aiIsNachtTarif/getFareEstimate der Cloud Functions — bei Änderungen alle
// drei Schichten synchron halten. Anfahrtskosten kommen separat (siehe anfahrt.js).

const num = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const parseTimeToMinutes = (hhmm) => {
  const [h, m] = String(hhmm ?? '').split(':');
  return (Number(h) || 0) * 60 + (Number(m) || 0);
};

/**
 * Gilt zum gegebenen Zeitpunkt der Nachttarif? Für Vorbestellungen die geplante
 * Abholzeit übergeben, sonst „jetzt". Nutzt die lokale (deutsche) Browserzeit —
 * die CF rechnet explizit in Europe/Berlin, der Disponent sitzt in derselben Zone.
 */
export const isNachtTarif = (settings, dateTime = new Date()) => {
  if (!settings || settings.nachtTarifAktiv !== true) return false;
  const dt = dateTime instanceof Date ? dateTime : new Date(dateTime);
  if (Number.isNaN(dt.getTime())) return false;
  const minutes = dt.getHours() * 60 + dt.getMinutes();
  const isWeekend = dt.getDay() === 0 || dt.getDay() === 6; // So/Sa
  const startStr = isWeekend
    ? (settings.nachtTarifWochenendStart ?? '20:00')
    : (settings.nachtTarifWocheStart ?? '22:00');
  const endStr = isWeekend
    ? (settings.nachtTarifWochenendEnd ?? '07:00')
    : (settings.nachtTarifWocheEnd ?? '06:00');
  const start = parseTimeToMinutes(startStr);
  const end = parseTimeToMinutes(endStr);
  if (start <= end) {
    return minutes >= start && minutes < end;
  }
  // Fenster mit Mitternachts-Übergang (z. B. 22:00–06:00)
  return minutes >= start || minutes < end;
};

export const effectiveGrundpreis = (settings, dateTime) =>
  isNachtTarif(settings, dateTime)
    ? num(settings.nachtTarifGrundpreis, 3.5)
    : num(settings.tarifGrundpreis, 3.5);

export const effectivePreisProKm = (settings, dateTime) =>
  isNachtTarif(settings, dateTime)
    ? num(settings.nachtTarifPreisProKm, 2.5)
    : num(settings.tarifPreisProKm, 2.5);

/**
 * Geschätzter Fahrpreis (ohne Anfahrt) nach Taxameter-Tarif, auf volle 10 Cent
 * gerundet — wie _calculateEstimatedEarningEur (App) und getFareEstimate (CF).
 * Großraum/Kleinbus (vehicleType >= 2) erhält den Großraumzuschlag.
 *
 * @returns {number|null} null, wenn keine Distanz vorliegt.
 */
export const calculateEstimatedFareEur = (
  settings,
  { distanceKm, vehicleType = 1, dateTime = new Date() }
) => {
  if (!settings || !Number.isFinite(distanceKm)) return null;
  let fare =
    effectiveGrundpreis(settings, dateTime) +
    distanceKm * effectivePreisProKm(settings, dateTime);
  if (Number(vehicleType) >= 2) {
    fare += num(settings.tarifGrossraumzuschlag, 5.0);
  }
  return Math.round(fare * 10) / 10;
};
